import React from 'react'
import {BiobankInput} from "../components/BiobankRequest";
import util from "../lib/chart_functions";
import i18n from "i18next";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import Accordion from "../components/Accordion";

export default class BiobankContainer extends React.Component {

    constructor(props) {
        super(props);

        // Read URL params for pre-selected filters
        const urlParams = new URLSearchParams(window.location.search);
        const urlCollection = urlParams.get('collection');
        const urlType = urlParams.get('type');

        this.state = {
            response: '[]',
            status_code: 200,
            isLoading: true,
            selector_names: ['type_list', 'collection_list'],
            lists: [],
            checkeds: [],
            // Store URL filters to apply after selectors are loaded
            urlFilters: {
                collection: urlCollection ? urlCollection.split(',') : null,
                type: urlType ? urlType.split(',') : null
            }
        };
    }

    componentDidMount() {
        const headers = {'Access-Control-Request-Method': 'GET'}
        const params = new URLSearchParams({
            full: 'true',
            k_cutoff: '5',
            use_similarity: 'false'
        });

        // If URL filters exist, add them to the initial request
        const { urlFilters } = this.state;
        if (urlFilters.collection) {
            params.set('collection', urlFilters.collection.join(','));
        }
        if (urlFilters.type) {
            params.set('type', urlFilters.type.join(','));
        }

        this.handleFetch(`${process.env.REACT_APP_BACK_END}/biobanks/?${params.toString()}`, {headers});
    }

    createNewRequest = (input) => {
        const headers = {'Content-Type': 'application/json'}
        const params = new URLSearchParams({
            type: input.type_input,
            collection: input.collection_input,
            similarity_cutoff: input.similarity_input / 100,
            k_cutoff: input.k_phenos_input,
            use_similarity: input.use_similarity_checked[0]
        });
        this.handleFetch(`${process.env.REACT_APP_BACK_END}/biobanks/?${params.toString()}`, {headers});
    }

    handleFetch = (url, options) => {
        this.setState({isLoading: true});
        fetch(url, options)
            .then(response => {
                this.setState({status_code: response.status})
                return response.text()
            })
            .then(data => this.setState({response: data, isLoading: false}))
            .catch(error => this.setState({status_code: 500, response: error.message, isLoading: false}));
    }

    countItems = (data, category) => {
        if (!data || !data[category]) return 0;
        const items = data[category];
        if (Array.isArray(items)) return items.length;
        if (typeof items === 'object') return Object.keys(items).length;
        return 0;
    }

    render() {
        const {response} = this.state
        const {status_code} = this.state
        let {lists} = this.state
        let {checkeds} = this.state
        const {selector_names} = this.state
        const {isLoading} = this.state

        const ERRORS = ["assays_zero", "patients_zero", "organizations_zero", "collections_zero", "merge_zero"]

        // Helper to render BiobankInput with default/current props
        const renderBiobankInput = (overrides = {}) => (
            <BiobankInput createNewRequest={this.createNewRequest}
                          collection={overrides.collection || lists['collection_list'] || []}
                          collection_checked={overrides.collection_checked || checkeds['collection_list'] || []}
                          type={overrides.type || lists['type_list'] || []}
                          type_checked={overrides.type_checked || checkeds['type_list'] || []}
            />
        );

        if (isLoading) {
            return (<div className="main-container container">
                <div className="row">
                    <div className="col-4">
                        {renderBiobankInput()}
                    </div>
                    <div className="col-8">
                        <LoadingSpinner message="loading_data" size="md" />
                    </div>
                </div>
            </div>);
        }

        if (status_code !== 200) {
            const errorType = status_code >= 500 ? 'error_server' : 'error_network';
            return (<div className="main-container container">
                <div className="row">
                    <div className="col-4">
                        {renderBiobankInput()}
                    </div>
                    <div className="col-8">
                        <ErrorMessage
                            type="error"
                            message={errorType}
                            details={`Status: ${status_code}`}
                            onRetry={() => this.componentDidMount()}
                        />
                    </div>
                </div>
            </div>);
        }

        if (ERRORS.includes(response)) {
            return (<div className="main-container container">
                <div className="row">
                    <div className="col-4">
                        {renderBiobankInput()}
                    </div>
                    <div className="col-8">
                        <ErrorMessage type="empty" message={response} />
                    </div>
                </div>
            </div>);
        }

        // Success case
        const parsed_json = JSON.parse(response)

        let gwases = parsed_json["gwases"]
        let phenotypes = parsed_json["phenotypes"]
        let ncbi = parsed_json["ncbi"]
        const selectors = parsed_json["selectors"]

        lists = []
        checkeds = []
        const { urlFilters } = this.state;

        for (const s in selector_names) {
            let s_name = selector_names[s];
            let list = []
            let checked = []
            if (typeof selectors !== 'undefined') {
                list = selectors[s_name]

                // Determine which filter to apply based on selector name
                let urlFilterValues = null;
                if (s_name === 'collection_list' && urlFilters.collection) {
                    urlFilterValues = urlFilters.collection;
                } else if (s_name === 'type_list' && urlFilters.type) {
                    urlFilterValues = urlFilters.type;
                }

                // If URL filters exist for this selector, check only matching items
                if (urlFilterValues) {
                    checked = list.map(item => urlFilterValues.includes(item));
                } else {
                    // Default: all checked
                    checked = Array(list.length).fill(true);
                }
            }
            lists[s_name] = list;
            checkeds[s_name] = checked;
        }

        const phenoCategories = ['category_fg', 'category_ukb'];
        const gwasCategories = ['category_fg', 'category_ukb', 'category_meta'];

        return (<div className="main-container container">
            <div className="row">
                <div className="col-4">
                    {renderBiobankInput()}
                </div>
                <div className="col-8">
                    <Accordion>
                        {/* Phenotypes Section */}
                        <Accordion.Item
                            title={i18n.t("found_phenotypes_header")}
                            defaultOpen={true}
                        >
                            {typeof phenotypes !== 'undefined' && (
                                <Accordion>
                                    {phenoCategories.map((category, idx) => {
                                        const items = phenotypes[category];
                                        const count = Array.isArray(items) ? items.length : 0;
                                        return (
                                            <Accordion.Item
                                                key={`pheno-${idx}`}
                                                title={i18n.t(`title_${category}`)}
                                                badge={count}
                                                defaultOpen={idx === 0}
                                            >
                                                {util.jsonToList(items)}
                                            </Accordion.Item>
                                        );
                                    })}
                                </Accordion>
                            )}
                        </Accordion.Item>

                        {/* GWAS Section */}
                        <Accordion.Item
                            title={i18n.t("found_gwas_header")}
                            defaultOpen={true}
                        >
                            {typeof gwases !== 'undefined' && (
                                <Accordion>
                                    {gwasCategories.map((category, idx) => {
                                        const items = gwases[category];
                                        const count = Array.isArray(items) ? items.length : 0;
                                        return (
                                            <Accordion.Item
                                                key={`gwas-${idx}`}
                                                title={i18n.t(`title_${category}`)}
                                                badge={count}
                                                defaultOpen={idx === 0}
                                            >
                                                {util.jsonToTable(items)}
                                            </Accordion.Item>
                                        );
                                    })}
                                </Accordion>
                            )}
                        </Accordion.Item>

                        {/* NCBI Section */}
                        <Accordion.Item
                            title={i18n.t("found_ncbi")}
                            badge={Array.isArray(ncbi) ? ncbi.length : 0}
                            defaultOpen={false}
                        >
                            {typeof ncbi !== 'undefined' && util.jsonToList(ncbi)}
                        </Accordion.Item>
                    </Accordion>
                </div>
            </div>
        </div>);
    }

}
